import React from 'react'
import _ from 'lodash'

import {FormRow, Select, AddressSearch} from '../UI/forms/index.js'
import compareAddresses from '../../../utils/compareAddresses.js'

// A stop of the route only lends its address to the start or end point: its order, index, type and stop number would
// make the start or end point count as a stop of that order when the route is optimized
const ROUTE_POINT_FIELDS = ['name', 'attention', 'street', 'nr', 'addition', 'street2', 'postalCode', 'city', 'country', 'email', 'phone', 'isBusiness', 'position']

export default class ChooseRoutePoint extends React.Component {
    constructor(props) {
        super(props)

        const {defaultOption = 'companyLocation'} = props

        this.state = {
            addressOption: defaultOption,
            addressField: props.address,
            edit: false,
            positionError: false
        }
    }

    componentDidMount() {
        const {defaultOption = 'companyLocation', address, reseller, users, route} = this.props

        if (!address) {
            const initialOption = reseller?.settings?.branches?.length > 0 ? 'branch-0' : defaultOption

            this.setState({addressOption: initialOption})
            this.onAddressOptionChange({
                target: {
                    value: initialOption
                }
            })
        } else {
            let addressOption = 'address'

            if (compareAddresses(address, users?.[route.messengerId]?.address)) {
                addressOption = 'messengerAddress'
            } else {
                let matched = false

                if (reseller?.settings?.branches?.length > 0) {
                    for (const [index, branch] of reseller.settings.branches.entries()) {
                        if (compareAddresses(address, branch.address)) {
                            addressOption = `branch-${index}`
                            matched = true
                            break
                        }
                    }
                } else if (compareAddresses(address, reseller?.settings?.companyAddress)) {
                    addressOption = 'companyLocation'
                    matched = true
                } else {
                    for (const [index, sublocation] of reseller.settings.sublocations.entries()) {
                        if (compareAddresses(address, sublocation)) {
                            addressOption = `sublocation-${index}`
                            matched = true
                            break
                        }
                    }
                }

                if (!matched && route?.addresses?.length) {
                    for (const [index, routeAddress] of route.addresses.entries()) {
                        if (compareAddresses(address, routeAddress)) {
                            addressOption = `routeAddress-${index}`
                            break
                        }
                    }
                }
            }

            this.setState({addressOption})
        }
    }

    onConfirm(address) {
        const {onChange} = this.props

        if (address && address.position) {
            delete address.id
            onChange(address)
            this.setState({edit: false})
        } else {
            this.setState({positionError: true})
        }
    }

    async onGetAddress() {
        const position = await new Promise((resolve, reject) => {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(resolve, reject)
            }
        })

        return {
            lat: position.coords.latitude, lng: position.coords.longitude, accuracy: position.coords.accuracy
        }
    }

    async onAddressOptionChange(event) {
        const {route, reseller, users, user} = this.props
        const option = event.target.value
        let address

        if (option.includes('routeAddress')) {
            const addressIndex = option.split('-')[1]
            address = _.pick(route.addresses[addressIndex], ROUTE_POINT_FIELDS)
        } else if (option === 'address') {
            this.setState({addressField: {}})
        } else if (option === 'companyLocation') {
            address = reseller.settings.companyAddress
        } else if (option.includes('sublocation')) {
            const addressIndex = option.split('-')[1]
            address = reseller.settings.sublocations[addressIndex]
        } else if (option.includes('branch')) {
            const addressIndex = option.split('-')[1]
            address = reseller.settings.branches?.[addressIndex]?.address
        } else if (option === 'messengerAddress') {
            if (users[route.messengerId]?.address) {
                address = users[route.messengerId]?.address
            }
        } else if (option === 'userAddress') {
            address = user.address
        } else if (option === 'currentLocation') {
            const position = await this.onGetAddress()
            address = {
                name: 'Huidige locatie',
                street: '',
                street2: '',
                nr: '',
                postalCode: '',
                city: '',
                country: '',
                position
            }
        }

        if (option !== 'address') {
            this.setState({addressField: address})
            this.onConfirm(address)
        }

        this.setState({addressOption: option})
    }

    onChangeAddressField(address) {
        this.setState({addressField: address})
        this.onConfirm(address)
    }

    render() {
        const {addressOption, addressField, positionError} = this.state
        const {reseller, users, user, route, label, disabled, readOnly, selectStyle} = this.props

        const addressOptions = []

        if (reseller.settings.branches?.length > 0) {
            reseller.settings.branches.map((branch, index) => {
                addressOptions.push({
                    title: branch.name,
                    value: `branch-${index}`,
                    bold: true
                })
            })
        } else {
            addressOptions.push({
                title: 'Bedrijfslocatie',
                value: 'companyLocation',
                bold: true
            })

            reseller.settings.sublocations?.map((address, index) => {
                addressOptions.push({
                    title: address.name,
                    value: `sublocation-${index}`,
                    bold: true
                })
            })
        }

        if (user) {
            addressOptions.push(
                {
                    title: 'Huidige locatie',
                    value: 'currentLocation',
                    bold: true
                },
                {
                    title: 'Mijn adres',
                    value: 'userAddress',
                    bold: true
                }
            )
        }

        if (users?.[route.messengerId]?.address) {
            addressOptions.push({
                title: 'Adres van koerier',
                value: 'messengerAddress',
                bold: true
            })
        }

        addressOptions.push({
            title: 'Anders:',
            value: 'address',
            bold: true
        })

        route?.addresses?.map((address, index) => {
            addressOptions.push({
                title: `${address.street} ${address.nr}${address.addition}, ${address.city}`,
                value: `routeAddress-${index}`
            })
        })

        return (
            <FormRow>
                <Select
                    label={label}
                    value={addressOption}
                    onChange={this.onAddressOptionChange.bind(this)}
                    options={addressOptions}
                    disabled={disabled}
                    readOnly={readOnly}
                    info={positionError && 'Dit adres staat niet in onze database, controleer of dit adres geldig is.'}
                    infoIcon='mdi mdi-alert'
                    style={selectStyle}
                />

                <AddressSearch
                    address={addressField}
                    readOnly={addressOption !== 'address'}
                    onChange={this.onChangeAddressField.bind(this)}
                    style={selectStyle}
                />
            </FormRow>
        )
    }
}
